import { useEffect, useRef, type ReactNode } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Omitted for a dialog that is all header and footer — no empty body is drawn. */
  children?: ReactNode;
  /** Buttons, right-aligned in a bordered strip at the bottom. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
}

const SIZE = {
  sm: 'max-w-md',
  md: 'max-w-xl',
  lg: 'max-w-3xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
} as const;

/**
 * Dialog built on the native `<dialog>` element, which gives us the top layer,
 * focus trapping and Escape-to-close from the platform instead of from a
 * hand-rolled key handler that will eventually get one of them wrong.
 */
let openModalCount = 0;

function lockScroll() {
  openModalCount += 1;
  if (openModalCount === 1) {
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
  }
}

function unlockScroll() {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
  }
}

export function Modal({
  isOpen,
  onClose,
  onBack,
  title,
  description,
  children,
  footer,
  size = 'md',
}: ModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      if (!dialog.open) dialog.showModal();
      lockScroll();
      return () => {
        unlockScroll();
      };
    } else {
      if (dialog.open) dialog.close();
    }
  }, [isOpen]);

  return (
    <dialog
      ref={dialogRef}
      // Escape fires `cancel`; the backdrop click is handled below.
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        // A click that lands on the dialog element itself is a backdrop click —
        // the panel inside stops propagation.
        if (event.target === dialogRef.current) onClose();
      }}
      aria-labelledby="modal-title"
      className={cn(
        /*
          Centred by this rule rather than by the browser's own.

          A modal `<dialog>` is centred by the user agent with
          `position: fixed; inset: 0; margin: auto`, and the `relative` that
          used to sit here overrode the first of those. Chrome centres it
          anyway; a browser that takes the declaration at its word lays the
          dialog out in normal flow instead and it opens against the top of the
          page. Stated in full here so every browser puts it in the same place:
          pinned to the viewport, height to its content, and auto margins on all
          four sides to centre it — `h-fit` being what leaves the vertical
          margins something to divide.

          `fixed` also makes this the containing block for the decorative layer
          below, which is what `relative` was doing for it.
        */
        'fixed inset-0 z-50 m-auto h-fit w-[calc(100vw-2rem)]',
        'overflow-hidden rounded-2xl sm:rounded-3xl bg-surface p-0 text-ink',
        'border border-line/90 dark:border-white/10 shadow-2xl shadow-black/30 dark:shadow-black/80',
        'backdrop:bg-slate-950/65 backdrop:backdrop-blur-xs',
        'animate-fade-up open:flex open:flex-col',
        SIZE[size],
      )}
    >
      {/*
        The paper the form is written on.
 
        A dialog opens over a dimmed page with nothing of its own behind it, so a
        long form reads as a blank sheet. A drifting field of dots and two soft
        lights give it a surface — behind the fields rather than in among them,
        because a panel at the top of a dialog is one more thing between somebody
        and the first question they came to answer.
 
        `pointer-events-none` throughout: a click that lands here still reaches
        the dialog element, which is what the backdrop-close check below reads.
        `aria-hidden` too — none of it says anything.
      */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 dot-field animate-dot-drift opacity-70" />
        <div className="absolute -left-20 -top-24 h-48 w-48 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-500/10 blur-3xl animate-orb" />
        <div className="absolute -bottom-28 -right-20 h-52 w-52 rounded-full bg-gradient-to-br from-sky-300/20 to-blue-400/10 blur-3xl animate-orb-slow" />
      </div>

      <div
        onClick={(event) => event.stopPropagation()}
        className="relative flex max-h-[85vh] flex-col"
      >
        <header className="flex items-start justify-between gap-4 border-b border-line bg-surface/80 px-5 py-4 backdrop-blur-sm">
          <div className="flex items-center gap-3 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                aria-label="Back"
                title="Back"
                className="-ml-1 shrink-0 rounded-xl p-1.5 text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink cursor-pointer"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0">
              <h2 id="modal-title" className="text-xl font-bold text-slate-700 dark:text-white">
                {title}
              </h2>
              {description && <p className="mt-1 text-sm text-ink-2 font-medium">{description}</p>}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 shrink-0 rounded-xl p-2 text-ink-3 transition-colors hover:bg-surface-2 hover:text-ink cursor-pointer"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {children != null && children !== false && (
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5 custom-scrollbar">
            {children}
          </div>
        )}

        {footer && (
          <footer className="flex items-center justify-end gap-2 border-t border-line bg-surface/80 px-5 py-3.5 backdrop-blur-sm">
            {footer}
          </footer>
        )}
      </div>
    </dialog>
  );
}
